/**
 * Error Handler Utility
 * Handles API errors gracefully and provides user-friendly messages
 */

import { getErrorMessage as getMappedErrorMessage } from '@/utils/errorMessages';

/**
 * Check if response indicates an empty/not found state
 * @param {Object} response - API response
 * @returns {boolean}
 */
export const isEmptyResponse = (response) => {
  return response?.data?.isEmpty === true || 
         response?.data?.error === 'ORGANIZATION_NOT_FOUND' ||
         response?.data?.error === 'ROUTE_NOT_FOUND';
};

/**
 * Extract error message from API error response
 * Prefers response.data.details, then response.data.message; avoids showing
 * generic "Request failed with status code XXX" in toasts.
 * @param {Object} error - Error object from catch block
 * @param {string} fallbackMessage - Fallback message if no error message found
 * @returns {string}
 */
export const extractErrorMessage = (error, fallbackMessage = 'حدث خطأ غير متوقع') => {
  const data = error?.response?.data;

  // Prefer API response body: details (actual error message) then message
  if (data) {
    if (data.details != null) {
      const detailsStr = Array.isArray(data.details)
        ? data.details.map((d) => (typeof d === 'string' ? d : d?.message ?? String(d))).filter(Boolean).join(' ')
        : typeof data.details === 'string' ? data.details : String(data.details);
      if (detailsStr.trim()) return detailsStr;
    }
    if (data.message != null && String(data.message).trim()) {
      return typeof data.message === 'string' ? data.message : String(data.message);
    }
  }

  // Only use error.message if it's not the generic axios status text
  const rawMessage = error?.message;
  if (rawMessage && !/^Request failed with status code \d{3}$/i.test(rawMessage)) {
    return rawMessage;
  }

  return fallbackMessage;
};

/**
 * Get user-friendly error message
 * @param {Object} error - Error object or response
 * @returns {string}
 */
export const getErrorMessage = (error) => {
  // If it's a handled empty response
  if (isEmptyResponse(error)) {
    return error.data?.message || 'البيانات غير متوفرة حالياً';
  }

  // Use centralized error message mapping
  return getMappedErrorMessage(error);
};

/**
 * Get data from response, handling empty states
 * @param {Object} response - API response
 * @param {*} defaultValue - Default value if data is empty
 * @returns {*}
 */
export const getResponseData = (response, defaultValue = []) => {
  // Check if it's an empty response
  if (isEmptyResponse(response)) {
    console.warn('📭 استجابة فارغة:', getErrorMessage(response));
    return defaultValue;
  }

  // Return the data
  return response?.data?.data ?? response?.data ?? defaultValue;
};

/**
 * Show a user-friendly notification for empty responses
 * @param {Object} response - API response
 * @returns {boolean} - true if it's an empty response
 */
export const handleEmptyResponse = (response) => {
  if (isEmptyResponse(response)) {
    const message = getErrorMessage(response);
    console.info('ℹ️', message);
    
    // You can integrate with a toast notification library here
    // For example: toast.info(message);
    
    return true;
  }
  return false;
};

/**
 * Wrap async API calls with error handling
 * @param {Function} apiCall - API function to call
 * @param {*} fallbackData - Data to return on error
 * @returns {Promise}
 */
export const safeApiCall = async (apiCall, fallbackData = null) => {
  try {
    const response = await apiCall();
    
    // Handle empty responses gracefully
    if (isEmptyResponse(response)) {
      handleEmptyResponse(response);
      return { success: false, data: fallbackData, isEmpty: true };
    }
    
    return { success: true, data: getResponseData(response, fallbackData) };
  } catch (error) {
    console.error('Error in API call:', error);
    const message = getErrorMessage(error);
    
    return { 
      success: false, 
      data: fallbackData, 
      error: message,
      isEmpty: false 
    };
  }
};

export default {
  isEmptyResponse,
  getErrorMessage,
  extractErrorMessage,
  getResponseData,
  handleEmptyResponse,
  safeApiCall
};

